import { RefreshControl } from 'react-native';
import { View, SafeAreaView, SxProp, ScrollView } from 'dripsy';
import React, { useContext } from 'react';

import { UserEarnOfferListResponse } from '@utx/utx-client';
import { LayoutContext } from 'contexts/LayoutContext';
import { useDiscoverNative } from './hooks/useDiscoverNative.hook';
import { ImageSlider } from '@/design-system/components/imageSlider/ImageSlider';
import { Icon } from '@/design-system/components/icons/Icon';
import { PopularOffers, PopularOffersVariant } from '../../components/discover/PopularOffers';
import { Footer } from '@/design-system/components/footer/Footer';
import { H6, TextCaption } from '@/design-system/components/typography';
import { t } from 'i18next';

const BANNER_HEIGHT_RATIO = 0.4664723;

export interface DiscoverHomeProps {
  loading: boolean;
  isNewAccount: boolean;
  offers: UserEarnOfferListResponse['data'];
  popularOffers: UserEarnOfferListResponse['data'];
  refresh: () => void;
  openAccount: () => void;
  onSearch: (value?: string) => void;
  showAssets: {
    showMyAssetsOnly: boolean;
    toggleShowMyAssetsOnly: () => void;
  };
  showAssetsPoular: {
    showMyAssetsOnly: boolean;
    toggleShowMyAssetsOnly: () => void;
  };
}

export const DiscoverHome: React.FC<DiscoverHomeProps> = ({ loading, offers, refresh, onSearch, showAssets, showAssetsPoular }) => {
  const { displayOffers, bottomSheetFilterProps } = useDiscoverNative({ offers });

  const layout = useContext(LayoutContext);
  const containerWidth = layout.dimensions.width - 32;

  return (
    <SafeAreaView sx={styles.container}>
      <ScrollView
        sx={styles.scrollContent}
        nestedScrollEnabled
        refreshControl={<RefreshControl onRefresh={refresh} refreshing={loading} />}
        contentContainerSx={styles.content}>
        {/* Carousel */}
        <View sx={styles.carousel}>
          <ImageSlider>
            <View sx={{ width: containerWidth, height: containerWidth * BANNER_HEIGHT_RATIO }}>
              <Icon name="discoverHomeBannerNative" width={containerWidth} height={containerWidth * BANNER_HEIGHT_RATIO} />
              <View sx={{ ...styles.carouselTextGroup, height: containerWidth * BANNER_HEIGHT_RATIO }}>
                <H6 bold sx={{ color: '$textTheme' }}>
                  {t('earn-invest::glossary.earn-and-invest')}
                </H6>
                <TextCaption semiBold sx={{ color: '$textDarkBackground' }}>
                  {t('earn-invest::glossary.subscription-to-offers')}
                </TextCaption>
              </View>
            </View>
          </ImageSlider>
        </View>
        {/* Popular Offers */}
        <View sx={styles.popularOffers}>
          <PopularOffers
            loading={loading}
            offers={displayOffers}
            variant={PopularOffersVariant.Native}
            showAssets={showAssetsPoular}
            onSearch={onSearch}
            bottomSheetFilterProps={bottomSheetFilterProps}
          />
        </View>
        <Footer />
      </ScrollView>
    </SafeAreaView>
  );
};

enum StyleName {
  content = 'content',
  container = 'container',
  carousel = 'carousel',
  carouselTextGroup = 'carouselTextGroup',
  buttonsGroup = 'buttonsGroup',
  viewAllOffers = 'viewAllOffers',
  popularOffers = 'popularOffers',
  popularOffersFilter = 'popularOffersFilter',
  popularOffersFilterCheckboxWrapper = 'popularOffersFilterCheckboxWrapper',
  disclaimer = 'disclaimer',
  scrollContent = 'scrollContent',
  productIconButton = 'productIconButton'
}

const styles: Record<StyleName, SxProp> = {
  container: {
    flex: 1,
    flexDirection: 'column',
    backgroundColor: '$backgroundSecondary'
  },
  scrollContent: {
    flex: 1,
    height: '100%',
    backgroundColor: '$backgroundSecondary'
  },
  content: {
    paddingX: 0,
    paddingY: '$5',
    backgroundColor: '$backgroundPrimary',
    borderTopLeftRadius: 25,
    borderTopRightRadius: 25
  },
  carousel: {
    flex: 1,
    mb: '$5',
    paddingX: '$4',
    overflow: 'hidden'
  },
  carouselTextGroup: {
    position: 'absolute',
    top: 0,
    left: 0,
    width: '70%',
    padding: '$3',
    gap: '$3'
  },
  buttonsGroup: {
    flexDirection: 'row',
    justifyContent: 'space-between'
  },
  viewAllOffers: {
    marginY: '$5'
  },
  popularOffers: {},
  popularOffersFilter: {
    flex: 1,
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center'
  },
  popularOffersFilterCheckboxWrapper: {},
  disclaimer: {
    mb: '$10'
    //
  },
  productIconButton: { flex: 1, width: '$15' }
};
